import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import './styles/theme.css'
import { AuthProvider } from './lib/auth'
import { ConfirmProvider } from './lib/confirm'
import { ApiError } from './lib/api'
import { registerServiceWorker } from './lib/swUpdate'
import App from './App'
import MobileGate from './components/MobileGate'
import InstallPrompt from './components/InstallPrompt'
import UpdateBanner from './components/UpdateBanner'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      // 401s are handled by the api layer's silent re-exchange; if one still
      // surfaces here the session is genuinely gone, so retrying is pointless.
      retry: (count, error) =>
        !(error instanceof ApiError && error.status === 401) && count < 2,
    },
  },
})

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <BrowserRouter>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <ConfirmProvider>
            <App />
            {/* Renders last so it paints above the app; returns null on desktop
                and when already running as an installed PWA. */}
            <MobileGate />
            {/* Desktop counterpart to the gate above: a PC is never blocked,
                but it can still install the app. */}
            <InstallPrompt />
            {/* Flags + forces a reload the moment a newly-deployed service
                worker takes over - see lib/swUpdate.ts. */}
            <UpdateBanner />
          </ConfirmProvider>
        </AuthProvider>
      </QueryClientProvider>
    </BrowserRouter>
  </StrictMode>,
)

/* The service worker is what makes the app installable at all - without a
 * registered SW the browser never offers "Install app", so the mobile gate
 * would have nothing to send users to. It also carries the auto-update
 * logic (periodic version checks + a forced reload onto any new version) -
 * see lib/swUpdate.ts. */
registerServiceWorker()
